import { MultiTabQuestionnaire } from "@smartintegrity/questionnaire";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { createQuestionnaireAiSuggestionExtension } from "../../../components/QuestionnaireAiSuggestionExtension";
import { createQuestionnaireAssessmentGapExtension } from "../../../components/QuestionnaireAssessmentGapExtension";
import { createQuestionnaireGapExtension } from "../../../components/QuestionnaireGapExtension";
import { composeQuestionExtensions } from "../../../components/composeQuestionExtensions";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { buildComplianceObligationsMap } from "src/modules/data-protection/helpers/complianceObligations";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";
import AssessmentPagesLayout from "../../../components/AssessmentPagesLayout";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{ width: "100%", height: "55px" }} className="dt-field dt-skeleton" />
                </Col>
            </Row>
        </div>
    );
};

const ConformityAssessmentQuestionnaire = ({
    questionsQuery,
    t,
    token,
    softwareId,
    conformityReportCaseId,
    onValidSubmit,
    onProgressChange,
    complianceObligations = []
}) => {
    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/report.update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess({ data }) {
            toast(t("Report Submitted"), {
                type: "success"
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("Error!"), {
                type: "error"
            });
        }
    });

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: "conformity_assessment",
            hardware: null,
            reportCase: conformityReportCaseId
        });
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId,
        conformityReportCaseId
    ]);

    const settings = useMemo(() => {
        return {
            tabSettings: {
                showNumber: true,
                showProgressBar: true
            },
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ""
            },
            /**
             * The layout renders Back / Next / Save below the panel, outside
             * the library's form.
             */
            buttonsSettings: {
                submit: {
                    display: false
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({ file }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`;
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: () => {
                        return `${API_BASE_URL}/report.uploadAttachment`;
                    }
                }
            },
            questions: {
                showNumber: true
            },
            pagesLayoutContext: {
                navigationTitle: "CE Conformity Sections",
                onProgressChange,
                isSaving: updateQuestionnaireMutation.isLoading
            }
        };
    }, [
        token,
        updateQuestionnaireMutation.isLoading,
        onProgressChange
    ]);

    const questionnaireComponents = useMemo(() => {
        const obligationsByCode = buildComplianceObligationsMap(
            complianceObligations
        );
        const QuestionExtension = composeQuestionExtensions(
            createQuestionnaireAiSuggestionExtension(),
            createQuestionnaireAssessmentGapExtension(),
            createQuestionnaireGapExtension(obligationsByCode)
        );

        return {
            PagesLayout: AssessmentPagesLayout,
            ...(QuestionExtension ? { QuestionExtension } : {})
        };
    }, [complianceObligations]);

    if (questionsQuery.isFetching) {
        return <QuestionsLoadingPlaceholder />;
    }

    return (
        <MultiTabQuestionnaire
            questionnaireUniqueId="conformity-assessment-questionnaire"
            className="gdpr"
            questions={questionsQuery.data?.mappedQuestions || []}
            defaultValues={questionsQuery.data?.mappedAnswers || []}
            settings={settings}
            components={questionnaireComponents}
            onSubmit={handleSubmitQuestionnaire}
        />
    );
};

export default withNamespaces()(
    connect((state) => {
        const { token } = state.Login;

        return {
            token
        };
    })(ConformityAssessmentQuestionnaire)
);
